import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  DockedCard,
  type DockedCardTone,
  Field,
  Icon,
  Input,
  Note,
  type NoteTone,
} from '@repo/design-system';
import { radius, semantic, space, type StatusRole, typeRole } from '@repo/design-tokens';
import { CameraPicture, type CameraWallFeed } from '@repo/ui';
import { useCameraNameCommit } from '@repo/ui/planner';
import { useEditorStore, useFloorplanStore } from '@repo/front-end-state';
import { cameraDisplayName, effectiveCoverage, findLatestDesign } from '@repo/front-end-utils';
import type { FloorPlannerCamera, RTSPEndpoint } from '@repo/frontend-types';

import { getCameraSight } from './cameraSight';
import {
  cameraNameHelper,
  cameraPanelSubtitle,
  cameraSightSentence,
  floorLabel,
  nameOnLeave,
} from './cameraPanelText';
import { streamDisplayName } from './streamDisplayName';
import CameraStreamSection from './CameraStreamSection';
import { calibrationStatus } from './calibrationStatusText';
import { targetPlanOf } from './calibrationModel';
import { useModelFootprints } from './useModelFootprints';
import { useImageNaturalSize } from './useImageNaturalSize';
import CalibrationRow from './CalibrationRow';
import CameraSightList from './CameraSightList';
import CoverageSliders from './CoverageSliders';
import { CameraFullScreenPicture } from './CameraHeatOverlay';

const { roles } = semantic.status;

/**
 * Each camera panel status and its role (epic onboarding-status-colours, Rob's status page): a new
 * camera is just changed, the open card for an existing one is being edited, no stream yet is work
 * to do, a stream gone from the twin is a problem (linking another fixes it), and what the camera
 * sees is information. The card and note tones are read from these roles, never named as hues.
 */
export const CAMERA_PANEL_ROLE = {
  newCamera: 'justChanged',
  existingCamera: 'beingEdited',
  noStream: 'workToDo',
  streamGone: 'problem',
  sees: 'information',
} as const satisfies Record<string, StatusRole>;

type CameraPanelRoleOf<K extends keyof typeof CAMERA_PANEL_ROLE> = (typeof CAMERA_PANEL_ROLE)[K];

/** The card's tone for a role: the role's own `Tag` tone, which `DockedCard` draws as a ribbon. */
const cardToneOf = (role: CameraPanelRoleOf<'newCamera' | 'existingCamera'>): DockedCardTone =>
  roles[role].tagTone;
/** A note's tone for a role: the role's own `Note` tone. */
const noteToneOf = (role: CameraPanelRoleOf<'streamGone' | 'sees'>): NoteTone =>
  roles[role].noteTone;

/**
 * Where the twin's stream list stands — the panel's picture says different things for each. A
 * refused session is `failed` here: the picture cannot be shown either way, and the step's own
 * banner says which.
 */
export type CameraPanelStreamsStatus = 'loading' | 'ready' | 'failed';

/**
 * The stream section's inputs (Change, Unlink, the stream picker). Camera Feeds gives them; Floor
 * Plan turns the section off, because linking is Camera Feeds' job (`unified-camera-panel`, R1.3).
 */
type CameraPanelStreamSection =
  | {
      /** Draw the stream section. Default true. */
      streamSection?: true;
      /** Every stream a camera holds, on ANY floor — the Stream picker offers only the rest. */
      heldStreamIds: ReadonlySet<number>;
      /** The wall's order, so the Stream picker lists streams as the wall's Not placed run does. */
      streamOrder?: (a: RTSPEndpoint, b: RTSPEndpoint) => number;
      /** Link this camera to a stream (the picker, or Change). One write, one draft save. */
      onLink: (refid: string, endpointId: number) => void;
      /** Unlink this camera's stream. One write, one draft save. */
      onUnlink: (refid: string) => void;
    }
  | {
      streamSection: false;
      heldStreamIds?: undefined;
      streamOrder?: undefined;
      onLink?: undefined;
      onUnlink?: undefined;
    };

interface CameraPanelBaseProps {
  /** The wall's feeds (`useCameraWallFeeds`), so the picture is the wall's own: health, frame, time. */
  feeds: readonly CameraWallFeed[];
  streamsStatus: CameraPanelStreamsStatus;
  /** The twin's streams, when read — the stream's name for the subtitle and the clear rule. */
  streams?: readonly RTSPEndpoint[];
  /** The camera was placed during this visit and the selection has not left it: the mint header. */
  isNew: boolean;
  /**
   * Write the selected camera. `report` false for a keystroke or a drag, true for a deliberate act —
   * the step asks its draft saver for a save only on true (`writeCamera`).
   */
  onWrite: (refid: string, patch: Partial<FloorPlannerCamera>, report: boolean) => void;
  /** Say something through the step's polite live region. */
  onAnnounce: (text: string) => void;
  /**
   * The full-screen picture is opening (`unified-camera-panel`, R5.7): Camera Feeds closes its
   * calibration screen. The panel opens and closes the picture itself, on both steps.
   */
  onPictureOpen?: () => void;
  /** Where focus goes when the panel closes. */
  returnFocusTo?: { readonly current: HTMLElement | null };
  /** Ask the planner to delete this camera through its own confirm. */
  onRequestDelete?: (refid: string) => void;
  /**
   * The camera's calibration (`observer-autogen-plan-projection`), as one line (`unified-camera-panel`,
   * R3). Only Camera Feeds gives it; absent, the panel draws no calibration row and no gap.
   */
  calibration?: {
    /** Open the calibration screen for this camera's picture. */
    onCalibrate: (feed: CameraWallFeed) => void;
  };
}

export type CameraPanelProps = CameraPanelBaseProps & CameraPanelStreamSection;

/**
 * The onboarding camera panel (`camera-feeds-page-panel`; `unified-camera-panel`): one panel for a
 * selected camera on Floor Plan and Camera Feeds, floating over the plan in the host's plan card
 * (`PlanCard`), so the plan never narrows for it. The header is mint (just changed) for a camera
 * placed during this visit and butter (being edited) for an existing one. Top to bottom: the
 * camera's picture at the wall's Frame size (a click or Enlarge opens it full screen, with the
 * people heat), the helper line, the calibration row (Camera Feeds only, `calibration`), the Name,
 * Turn, Widen and Reach (`CoverageSliders`), what the cone sees with its Show list
 * (`CameraSightList`), and the stream section — Change and Unlink, or the stream picker
 * (`CameraStreamSection`, `camera-feeds-page-link`), which Floor Plan turns off (`streamSection`).
 * Delete Camera and Done in the footer.
 *
 * It reads the selection and the plan from the stores, as the floating panel it replaced did, and
 * shows only while exactly one live camera on the active floor is selected. It is NOT keyed per
 * camera: selecting another camera re-targets the same panel (the header cross-fades), so the Name
 * commits its pending edit to the camera it was typed for on the change (`useCameraNameCommit`).
 * Every write goes out through `onWrite`.
 */
const CameraPanel: React.FC<CameraPanelProps> = props => {
  const {
    feeds,
    streamsStatus,
    streams,
    isNew,
    onWrite,
    onAnnounce,
    onPictureOpen,
    returnFocusTo,
    onRequestDelete,
    calibration,
  } = props;
  const layout = useFloorplanStore(s => s.layout);
  const activeFloorIndex = useFloorplanStore(s => s.activeFloorIndex);
  const selectedIds = useEditorStore(s => s.selectedEntityIds);

  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
  const floor = layout?.floors[activeFloorIndex];
  const design = floor ? findLatestDesign(floor.designs) : null;
  const liveCameras = design?.cameras?.filter(c => !c._deleted) ?? [];
  const cameraIndex = selectedId ? liveCameras.findIndex(c => c.refid === selectedId) : -1;
  const camera = cameraIndex >= 0 ? liveCameras[cameraIndex] : null;

  /*
   * The memo runs on EVERY render, before the early return below, so the hook order never changes
   * between "no camera" and "a camera". Keyed on the GEOMETRY, not the camera: the Name is typed in
   * this panel and writes the store per keystroke, and the cone intersection must not re-run for a
   * result a name cannot change. Walls occlude the cone, so a wall edit refreshes it too.
   */
  const sight = useMemo(
    () => (design && camera ? getCameraSight(design, camera) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      design?.surfaces,
      design?.items,
      design?.walls,
      camera?.refid,
      camera?.x,
      camera?.y,
      camera?.rotation,
      camera?.fovAngle,
      camera?.detectionDistance,
    ],
  );

  /*
   * Guided calibration R10: a calibration fitted against machine corners is out of date once one of
   * those machines has moved, so the status reads the targeted machines' sizes. Only those models
   * are read (the reads are shared with the calibration screen), and none for a calibration
   * without targets. Before a size is read, its machine is not checked.
   */
  const targetedFiles = useMemo(() => {
    // The stored value may be any shape: views that are not a list read as unreadable, never throw.
    const views = camera?.calibration?.views;
    const refids = new Set(
      (Array.isArray(views) ? views : []).flatMap(v =>
        Array.isArray(v?.pairs)
          ? v.pairs.flatMap(p => (p?.target ? [p.target.itemRefid] : []))
          : [],
      ),
    );
    return (design?.items ?? [])
      .filter(item => !item._deleted && refids.has(item.refid))
      .map(item => item.modelFile);
  }, [camera?.calibration, design?.items]);
  const targetFootprints = useModelFootprints(targetedFiles);
  const targetPlan = useMemo(
    () => targetPlanOf(design?.items ?? [], targetFootprints.arrived),
    [design?.items, targetFootprints.arrived],
  );

  const endpointId = camera?.rtspEndpointId ?? null;
  const stream = endpointId != null ? streams?.find(s => s.id === endpointId) : undefined;
  /** The stream's name from the read, when it has given it — the clear rule needs the real one. */
  const knownStreamName = stream ? streamDisplayName(stream) : undefined;
  /** What the panel CALLS the stream: the read's name, else what the camera caches. */
  const streamLabel =
    endpointId == null
      ? null
      : (knownStreamName ?? camera?.rtspEndpointBid ?? `Stream ${endpointId}`);
  const hasStream = endpointId != null;

  const name = useCameraNameCommit({
    refid: camera?.refid ?? '',
    name: camera?.name ?? '',
    onChange: (patch, { commit }, refid) => {
      if (refid) onWrite(refid, patch, commit);
    },
    resolveOnLeave: typed => nameOnLeave({ value: typed, hasStream, streamName: knownStreamName }),
    onResolved: stored =>
      onAnnounce(
        stored === null
          ? 'Kept the name: this stream’s name hasn’t loaded.'
          : `Name back to ${stored}.`,
      ),
  });

  /** The wall's feed for this camera's stream: its picture, and the calibration's picture. */
  const feed = hasStream ? feeds.find(f => Number(f.id) === endpointId) : undefined;
  /*
   * The picture's natural size, for the calibration status (R11: a calibration fitted on a picture of
   * another size is out of date). Before the early return, so the hook order never changes.
   */
  const frameSize = useImageNaturalSize(feed?.imageUrl);
  /** Calibrate: where focus goes once a calibration is removed and its Remove button is gone. */
  const calibrateRef = useRef<HTMLButtonElement | null>(null);

  /*
   * The full-screen picture (R5): open for the camera it was opened for, and gone the moment the panel
   * re-targets or its feed goes. It grows from the panel's picture; focus returns to what opened it.
   */
  const pictureRef = useRef<HTMLDivElement | null>(null);
  const [fullScreen, setFullScreen] = useState<{
    refid: string;
    opener: HTMLElement | null;
  } | null>(null);
  const fullScreenOpen = !!fullScreen && fullScreen.refid === camera?.refid && !!feed;
  useEffect(() => {
    if (fullScreen && !fullScreenOpen) setFullScreen(null);
  }, [fullScreen, fullScreenOpen]);
  const openFullScreen = () => {
    if (!camera) return;
    /*
     * What focus returns to: the control that opened it (Enlarge, by key or click). A click on the
     * picture itself focuses nothing (its hit layer refuses focus), so focus returns to Enlarge, the
     * picture's own control, never to an element hidden from assistive tech or not focusable.
     */
    const active = document.activeElement;
    const enlarge =
      pictureRef.current?.querySelector<HTMLButtonElement>('button[aria-label^="Enlarge"]') ?? null;
    const opener =
      active instanceof HTMLButtonElement && active.getAttribute('aria-hidden') !== 'true'
        ? active
        : enlarge;
    onPictureOpen?.();
    setFullScreen({ refid: camera.refid, opener });
  };
  /** The stream's BID for its heat: the twin's stream list first, the camera's cached one second. */
  const rtspBid = stream?.bid ?? camera?.rtspEndpointBid ?? null;

  if (!design || !camera || !sight || !floor) return null;

  const displayName = cameraDisplayName(camera, cameraIndex);
  const { fovAngle, detectionDistance } = effectiveCoverage(camera);
  // A camera stored with no direction faces 0°, as the plan draws it.
  const rotation = camera.rotation ?? 0;
  const sees = cameraSightSentence({
    areas: sight.surfaces.length,
    items: sight.items.length,
    newCameraName: isNew ? displayName : undefined,
  });
  const close = () => useEditorStore.getState().clearSelection();

  const picture = !hasStream ? (
    <NoStream data-testid="camera-panel-no-stream">
      {/* The outline glyph: a camera with no feed (solid when linked), everywhere a camera shows. */}
      <Icon name="video" size="sm" />
      {props.streamSection === false
        ? 'No stream linked yet. Link one on Camera Feeds.'
        : 'No stream yet'}
    </NoStream>
  ) : feed ? (
    <CameraPicture feed={feed} size="frame" feedRef={pictureRef} onEnlarge={openFullScreen}>
      {/* A click anywhere on the picture opens it too; the keyboard's way is Enlarge. */}
      <PictureHit
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        // Never takes focus: a click opens the picture, and focus stays where the keyboard left it.
        onMouseDown={e => e.preventDefault()}
        onClick={openFullScreen}
      />
    </CameraPicture>
  ) : streamsStatus === 'failed' ? (
    // Not "No stream yet": the camera HAS a stream; we could not read it. Saying otherwise would be
    // the false claim the step's failed-read branches exist to prevent.
    <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
      This camera’s picture can’t be shown: the twin’s streams could not be read.
    </Note>
  ) : streamsStatus === 'ready' && !stream ? (
    // The read succeeded and this camera's stream is not in it — removed from the twin, or a stale
    // link. Neither loading nor "no stream": the camera points at something that is gone (review).
    // A problem with a fix: linking another stream mends it.
    <Note
      tone={noteToneOf(CAMERA_PANEL_ROLE.streamGone)}
      icon={<Icon name="circle-exclamation" size="xs" />}
    >
      This camera’s stream is no longer one of this twin’s streams.
    </Note>
  ) : (
    // Still being read, or read and the wall's feeds not yet seeded from it: card 21's canonical
    // skeleton, named with what the camera holds.
    <CameraPicture
      feed={{ id: endpointId ?? '', name: streamLabel ?? '', state: 'connecting' }}
      size="frame"
    />
  );

  return (
    <DockedCard
      placement="inset"
      data-planner-shortcuts="off"
      data-testid="camera-panel"
      tone={cardToneOf(isNew ? CAMERA_PANEL_ROLE.newCamera : CAMERA_PANEL_ROLE.existingCamera)}
      icon={<Icon name="video" variant={hasStream ? 'solid' : 'regular'} />}
      title={displayName}
      headingLevel={4}
      sub={cameraPanelSubtitle({
        isNew,
        streamName: streamLabel,
        floor: floorLabel(floor.name, activeFloorIndex),
      })}
      onClose={close}
      closeLabel="Close camera panel"
      returnFocusTo={returnFocusTo}
      footer={
        <>
          {onRequestDelete ? (
            <Button
              variant="text"
              tone="destructive"
              iconLeft={<Icon name="trash-can" />}
              onClick={() => onRequestDelete(camera.refid)}
            >
              Delete Camera
            </Button>
          ) : null}
          <FooterGap />
          <Button variant="primary" size="inline" onClick={close}>
            Done
          </Button>
        </>
      }
    >
      <Body>
        <section>
          {picture}
          <Helper>Drag the camera to move it, or use its handles or these sliders.</Helper>
        </section>

        {calibration ? (
          <CalibrationRow
            status={calibrationStatus(camera, {
              hasPicture: !!feed?.imageUrl,
              frameSize,
              targetPlan,
            })}
            calibrateRef={calibrateRef}
            onCalibrate={() => {
              if (feed) calibration.onCalibrate(feed);
            }}
            onRemove={() => {
              // R10, R11: the admin removes a calibration; nothing removes one by itself.
              onWrite(camera.refid, { calibration: undefined }, true);
              onAnnounce(`Calibration removed from ${displayName}.`);
              // The Remove button goes with the calibration: focus moves to Calibrate once it has
              // re-rendered, rather than falling to the page.
              requestAnimationFrame(() => calibrateRef.current?.focus());
            }}
          />
        ) : null}

        <Field
          label="Name"
          helper={cameraNameHelper({
            hasStream,
            name: camera.name ?? '',
            streamName: knownStreamName,
            displayName,
          })}
        >
          {w => <Input {...w} {...name} onGlass />}
        </Field>

        <CoverageSliders
          rotation={rotation}
          fovAngle={fovAngle}
          detectionDistance={detectionDistance}
          onWrite={(patch, report) => onWrite(camera.refid, patch, report)}
          onAnnounce={onAnnounce}
        />

        <CameraSightList
          sentence={sees}
          items={sight.items}
          surfaces={sight.surfaces}
          tone={noteToneOf(CAMERA_PANEL_ROLE.sees)}
          onAnnounce={onAnnounce}
        />

        {props.streamSection !== false ? (
          <CameraStreamSection
            refid={camera.refid}
            displayName={displayName}
            endpointId={endpointId}
            streamLabel={streamLabel}
            streams={streams}
            streamsStatus={streamsStatus}
            feeds={feeds}
            heldStreamIds={props.heldStreamIds}
            streamOrder={props.streamOrder}
            onLink={props.onLink}
            onUnlink={props.onUnlink}
          />
        ) : null}
      </Body>
      {fullScreenOpen && feed ? (
        <CameraFullScreenPicture
          // The box at the frame's own ratio once it has loaded, so the picture fills it unbarred.
          feed={
            frameSize && frameSize.height > 0
              ? { ...feed, aspectRatio: frameSize.width / frameSize.height }
              : feed
          }
          displayName={displayName}
          rtspBid={rtspBid}
          anchor={pictureRef.current}
          returnFocusTo={fullScreen?.opener ?? null}
          onClose={() => setFullScreen(null)}
        />
      ) : null}
    </DockedCard>
  );
};

export default CameraPanel;

/* Frames 04–07's body: 12 px between blocks, 16 px at the sides (T:1551). */
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
`;

/* The whole picture as the pointer's way into the full-screen picture, under the picture's own
   buttons (CameraPicture's hit layer). */
const PictureHit = styled.button`
  position: absolute;
  inset: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: zoom-in;
`;

const Helper = styled.p`
  margin: ${space[2]} 0 0;
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.secondary};
`;

/* Frame 07's "No stream yet" box, in place of the picture (epic question 4: page-local, from existing
   parts), as work to do: the role's wash, edge and ink, and the outline video glyph. */
const NoStream = styled.div`
  aspect-ratio: 16 / 6;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${space[2]};
  background: ${roles[CAMERA_PANEL_ROLE.noStream].wash};
  border: 1.5px solid ${roles[CAMERA_PANEL_ROLE.noStream].mark};
  border-radius: ${radius.md};
  font-size: ${typeRole.body2.fontSize};
  line-height: ${typeRole.body2.lineHeight};
  color: ${roles[CAMERA_PANEL_ROLE.noStream].ink};
`;

const FooterGap = styled.span`
  flex: 1;
`;
